import React, { useState, useEffect } from 'react';
import DepartmentTree from './DepartmentTree';
import api from '../../config/axios';
import { API_ENDPOINTS } from '../../config/api';
import { formatPhoneForDisplay } from './utils/formatUtils';

/**
 * 상위관리자 선택 공통 컴포넌트
 * @param {boolean} visible - 모달 표시 여부
 * @param {Function} onClose - 모달 닫기 콜백
 * @param {Function} onSelect - 상위관리자 선택 시 호출되는 콜백 (supervisorId, supervisorEmail, supervisorName)
 * @param {Function} onCancel - 취소 시 호출되는 콜백 (원래 값으로 복원하기 위해)
 * @param {number|null} currentUserId - 현재 사용자 ID (자기 자신 제외 및 무한루프 체크용)
 * @param {number|null} initialSupervisorId - 초기 상위관리자 ID
 * @param {Function} showAlert - 알림 표시 함수
 * @param {Array} allUsers - 전체 사용자 목록 (무한루프 체크용, 선택사항)
 * @param {number|null} divisionId - 사업장 ID (선택사항, 있으면 해당 사업장의 부서만 조회)
 */
const SupervisorSelection = ({
  visible = false,
  onClose,
  onSelect,
  onCancel,
  currentUserId = null,
  initialSupervisorId = null,
  showAlert,
  allUsers = [],
  divisionId = null
}) => {
  const [supervisorInputMode, setSupervisorInputMode] = useState('tree'); // 'tree' 또는 'name'
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);
  const [selectedDepartmentName, setSelectedDepartmentName] = useState('');
  const [departmentUsers, setDepartmentUsers] = useState([]);
  const [loadingDepartmentUsers, setLoadingDepartmentUsers] = useState(false);
  const [nameQuery, setNameQuery] = useState('');
  const [nameSearchUsers, setNameSearchUsers] = useState([]);
  const [searchingByName, setSearchingByName] = useState(false);
  const [nameSearchDone, setNameSearchDone] = useState(false);
  const [currentUserSubordinates, setCurrentUserSubordinates] = useState([]); // 현재 사용자의 하위 계층 사용자 목록

  // 현재 사용자의 하위 계층 조회 (무한루프 체크용)
  const fetchCurrentUserSubordinates = async (userId) => {
    if (!userId) {
      setCurrentUserSubordinates([]);
      return;
    }
    
    try {
      // 계층 구조 조회 API를 사용하여 하위 계층 정보 가져오기
      const response = await api.get(`/api/users/recommended/${userId}/hierarchy`);
      const hierarchyData = response.data || {};
      const lowerHierarchy = hierarchyData.lowerHierarchy || [];
      setCurrentUserSubordinates(lowerHierarchy);
    } catch (error) {
      console.error('현재 사용자의 하위 계층 조회 실패:', error);
      // 에러가 발생해도 계속 진행 (하위 계층 정보가 없어도 체크는 가능)
      setCurrentUserSubordinates([]);
    }
  };

  // 모달이 열릴 때 초기값 설정 및 하위 계층 조회
  useEffect(() => {
    if (visible) {
      setSupervisorInputMode('tree');
      setSelectedDepartmentId(null);
      setSelectedDepartmentName('');
      setDepartmentUsers([]);
      setNameQuery('');
      setNameSearchUsers([]);
      setNameSearchDone(false);
      
      // 현재 사용자의 하위 계층 조회 (무한루프 체크용)
      if (currentUserId) {
        fetchCurrentUserSubordinates(currentUserId);
      } else {
        setCurrentUserSubordinates([]);
      }
    }
  }, [visible, currentUserId]);

  // 부서별 사용자 목록 조회
  const fetchUsersByDepartment = async (departmentId) => {
    try {
      setLoadingDepartmentUsers(true);
      const response = await api.get(`/api/users/by-department/${departmentId}`);
      const users = response.data || [];
      setDepartmentUsers(users);
    } catch (error) {
      console.error('부서별 사용자 목록 조회 실패:', error);
      if (showAlert) {
        showAlert(error.response?.data?.error || '부서별 사용자 목록을 불러오는데 실패했습니다.');
      }
      setDepartmentUsers([]);
    } finally {
      setLoadingDepartmentUsers(false);
    }
  };

  // 부서 선택 핸들러
  const handleSelectDepartment = (departmentId, departmentName) => {
    setSelectedDepartmentId(departmentId);
    setSelectedDepartmentName(departmentName);
    fetchUsersByDepartment(departmentId);
  };

  // 무한루프 체크 함수 (선택한 상위관리자의 상위 계층에 현재 사용자가 있는지 확인)
  const checkCircularReference = (supervisorId, userId, visited = new Set()) => {
    if (visited.has(supervisorId)) {
      return true;
    }
    if (supervisorId === userId) {
      return true;
    }
    visited.add(supervisorId);
    const supervisor = allUsers.find(u => u.id === supervisorId) || 
                      departmentUsers.find(u => u.id === supervisorId) ||
                      nameSearchUsers.find(u => u.id === supervisorId);
    if (supervisor && supervisor.supervisorId) {
      return checkCircularReference(supervisor.supervisorId, userId, visited);
    }
    return false;
  };

  // 현재 사용자의 하위 계층에 선택한 사용자가 있는지 확인 (재귀적)
  const isInLowerHierarchy = (currentUserId, selectedUserId, visited = new Set(), allAvailableUsers = null) => {
    if (visited.has(currentUserId)) {
      return false; // 순환 참조 방지
    }
    if (currentUserId === selectedUserId) {
      return false; // 자기 자신은 하위가 아님
    }
    visited.add(currentUserId);
    
    // 모든 사용자 목록을 통합 (한 번만 생성)
    if (!allAvailableUsers) {
      const allUsersMap = new Map();
      [...allUsers, ...departmentUsers, ...nameSearchUsers, ...currentUserSubordinates].forEach(u => {
        if (!allUsersMap.has(u.id)) {
          allUsersMap.set(u.id, u);
        }
      });
      allAvailableUsers = Array.from(allUsersMap.values());
    }
    
    // 현재 사용자의 직접 하위 사용자들을 찾기
    const directSubordinates = allAvailableUsers.filter(u => u.supervisorId === currentUserId);
    
    // 직접 하위 사용자 중 선택한 사용자가 있는지 확인
    if (directSubordinates.some(u => u.id === selectedUserId)) {
      return true;
    }
    
    // 각 하위 사용자의 하위 계층을 재귀적으로 탐색
    for (const subordinate of directSubordinates) {
      if (isInLowerHierarchy(subordinate.id, selectedUserId, visited, allAvailableUsers)) {
        return true;
      }
    }
    
    return false;
  };

  // 사용자 선택 핸들러
  const handleSelectUser = (userId) => {
    if (!currentUserId) {
      // currentUserId가 없으면 체크하지 않음
      const selectedUser = departmentUsers.find(u => u.id === userId) || 
                           allUsers.find(u => u.id === userId) ||
                           nameSearchUsers.find(u => u.id === userId);
      const userName = selectedUser?.name || '';
      const userEmail = selectedUser?.email || '';
      
      if (onSelect) {
        onSelect(userId, userEmail, userName);
        if (onClose) {
          onClose();
        }
      }
      return;
    }

    // 1. 자기 자신을 선택하는 경우 방지
    if (userId === currentUserId) {
      if (showAlert) {
        showAlert('자기 자신을 상위관리자로 선택할 수 없습니다.');
      }
      return;
    }

    // 2. 선택한 사용자가 현재 사용자의 하위 계층에 있는지 확인
    if (isInLowerHierarchy(currentUserId, userId)) {
      if (showAlert) {
        showAlert('선택한 사용자는 현재 사용자의 하위 계층에 있어 상위관리자로 등록할 수 없습니다. 무한루프가 발생할 수 있습니다.');
      }
      return;
    }

    // 3. 선택한 상위관리자의 상위 계층에 현재 사용자가 있는지 확인 (기존 체크)
    if (checkCircularReference(userId, currentUserId)) {
      if (showAlert) {
        showAlert('선택한 상위관리자는 현재 사용자의 하위에 있어 무한루프가 발생할 수 있습니다.');
      }
      return;
    }
    
    // 선택된 사용자 정보 찾기
    const selectedUser = departmentUsers.find(u => u.id === userId) || 
                         allUsers.find(u => u.id === userId) ||
                         nameSearchUsers.find(u => u.id === userId);
    const userName = selectedUser?.name || '';
    const userEmail = selectedUser?.email || '';
    
    console.log('SupervisorSelection - handleSelectUser:', { userId, userName, userEmail, selectedUser });
    
    if (onSelect) {
      console.log('SupervisorSelection - calling onSelect with:', { userId, userEmail, userName });
      onSelect(userId, userEmail, userName);
      // onSelect 호출 후에는 onCancel을 호출하지 않고 모달만 닫기
      if (onClose) {
        onClose();
      }
    } else {
      console.warn('SupervisorSelection - onSelect callback is not defined');
      handleClose();
    }
  };

  const handleSearchByName = async () => {
    const keyword = nameQuery.trim();
    if (keyword.length < 2) {
      if (showAlert) {
        showAlert('이름은 2글자 이상 입력해주세요.');
      }
      return;
    }
    try {
      setSearchingByName(true);
      const response = await api.get(API_ENDPOINTS.USERS_SUPERVISOR_NAME_SEARCH, {
        params: { name: keyword }
      });
      setNameSearchUsers(response.data || []);
      setNameSearchDone(true);
    } catch (error) {
      console.error('상위관리자 이름 검색 실패:', error);
      setNameSearchUsers([]);
      setNameSearchDone(true);
      if (showAlert) {
        showAlert(error.response?.data?.error || '사용자 검색에 실패했습니다.');
      }
    } finally {
      setSearchingByName(false);
    }
  };

  // 모달 닫기 핸들러
  const handleClose = (skipCancel = false) => {
    if (!skipCancel && onCancel) {
      onCancel();
    }
    if (onClose) {
      onClose();
    }
  };

  if (!visible) return null;

  return (
    <div 
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-5xl mx-4 max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">상위관리자 선택</h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* 입력 모드 선택 탭 */}
          <div className="px-6 py-3 border-b border-gray-200 flex gap-2">
            <button
              onClick={() => setSupervisorInputMode('tree')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                supervisorInputMode === 'tree'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              부서 계층트리
            </button>
            <button
              onClick={() => setSupervisorInputMode('name')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                supervisorInputMode === 'name'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              이름 검색
            </button>
          </div>
          
          {supervisorInputMode === 'tree' ? (
            <div className="flex-1 overflow-hidden flex">
              {/* 부서 트리 영역 */}
              <div className="w-1/2 border-r border-gray-200 p-4 overflow-y-auto">
                <DepartmentTree
                  selectedDepartmentId={selectedDepartmentId}
                  onSelectDepartment={handleSelectDepartment}
                  divisionId={divisionId}
                />
              </div>
              {/* 사용자 목록 영역 */}
              <div className="w-1/2 p-4 overflow-y-auto">
                {selectedDepartmentId ? (
                  <>
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">
                        선택된 부서: {selectedDepartmentName}
                      </h4>
                    </div>
                    {loadingDepartmentUsers ? (
                      <div className="flex justify-center items-center py-8">
                        <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>
                    ) : (() => {
                      const activeUsers = (departmentUsers || []).filter(
                        u => u.id !== currentUserId && (u.isActive === true || u.isActive === undefined)
                      );
                      const hasNoSelectableUsers = activeUsers.length === 0;
                      return hasNoSelectableUsers ? (
                        <div className="text-center py-8 text-gray-500">
                          해당 부서에 활성화된 사용자가 없습니다.
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {activeUsers.map((user) => {
                            const isSelected = initialSupervisorId === user.id;
                            return (
                              <div
                                key={user.id}
                                onClick={() => handleSelectUser(user.id)}
                                className={`px-4 py-3 border-2 rounded-lg cursor-pointer transition-colors ${
                                  isSelected
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                <div className="font-medium text-gray-900">{user.name}</div>
                                <div className="text-xs text-gray-500 mt-1">{user.email}</div>
                                {user.departmentName && (
                                  <div className="text-xs text-gray-500 mt-1">부서: {user.departmentName}</div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    부서를 선택하면 해당 부서의 사용자 목록이 표시됩니다.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex-1 p-6 overflow-y-auto">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  이름
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={nameQuery}
                    onChange={(e) => {
                      setNameQuery(e.target.value);
                      setNameSearchDone(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSearchByName();
                      }
                    }}
                    placeholder="이름을 2글자 이상 입력하세요"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={handleSearchByName}
                    disabled={searchingByName}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex-shrink-0"
                  >
                    {searchingByName ? '검색 중' : '검색'}
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  같은 사업장의 활성 사용자를 이름으로 검색합니다.
                  <span className="text-red-600"> 계층 구조상 하위 사용자는 선택할 수 없습니다.</span>
                </p>
              </div>
              {searchingByName ? (
                <div className="flex justify-center items-center py-8">
                  <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              ) : nameSearchDone ? (
                (() => {
                  const selectableUsers = nameSearchUsers.filter(u => u.id !== currentUserId);
                  if (selectableUsers.length === 0) {
                    return (
                      <div className="text-center py-8 text-gray-500">
                        검색 결과가 없습니다.
                      </div>
                    );
                  }
                  return (
                    <div className="space-y-2">
                      {selectableUsers.map((user) => {
                        const isSelected = initialSupervisorId === user.id;
                        const phoneText = formatPhoneForDisplay(user.phone) || '-';
                        return (
                          <div
                            key={user.id}
                            onClick={() => handleSelectUser(user.id)}
                            className={`px-4 py-3 border-2 rounded-lg cursor-pointer transition-colors ${
                              isSelected
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <div className="font-medium text-gray-900">{user.name}</div>
                            <div className="text-xs text-gray-500 mt-1">{phoneText}</div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupervisorSelection;

